import React from 'react';
import { X, Zap, Radio, Wifi, WifiOff, Check, Clock } from 'lucide-react';
import type { QualityData, QualityThresholds, WinReason } from './types';

// 候选状态 → 中文标签
export const getStatusLabel = (status: string): string => {
  switch (status) {
    case 'pending': return '等待';
    case 'offer_sent': return '已发Offer';
    case 'offer_received': return '收到Offer';
    case 'racing': return 'ICE协商';
    case 'testing': return '测试中';
    case 'connected': return '已连接';
    case 'failed': return '失败';
    case 'cancelled': return '已取消';
    default: return status;
  }
};

// ICE 子状态 → 中文标签
export const getIceStateLabel = (iceState?: string): string => {
  if (!iceState) return '';
  switch (iceState) {
    case 'new': return '初始化';
    case 'checking': return '检查中';
    case 'connected': return '已连接';
    case 'completed': return '完成';
    case 'failed': return '失败';
    case 'disconnected': return '断开';
    case 'closed': return '已关闭';
    default: return iceState;
  }
};

// 连接类型 → 显示（relay=琥珀，其余直连=绿）
export const connTypeView = (t?: string): { label: string; cls: string } =>
  t === 'relay'
    ? { label: 'RELAY', cls: 'bg-amber-500/30 text-amber-400' }
    : { label: 'P2P', cls: 'bg-emerald-500/30 text-emerald-400' };

// 胜出原因 → 文案
export const winReasonText = (r?: WinReason): string => {
  switch (r) {
    case 'fast-win': return '「UDP 快速胜出」(质量达标，提前选定)';
    case 'quality-select': return '「质量测试选优」(测满后评分最高)';
    case 'timeout-fallback': return '「超时兜底」(超时前唯一连通线路)';
    case 'forceRelay': return '「强制中转」(forceRelay 模式首个就绪)';
    default: return '';
  }
};

// PASS/FAIL 判定（与 IceRacingService onFinalTimeout 同口径）
// 返回 null 表示无质量数据（不显示判定）
export const evaluatePass = (q: QualityData | undefined, th: QualityThresholds): boolean | null => {
  if (!q || q.packetsReceived === 0) return null;
  const total = q.packetsReceived + q.packetsLost;
  const lossPct = total > 0 ? (q.packetsLost / total) * 100 : 0;
  const jitter = q.rttJitterMs ?? 0;
  return q.throughputKbps >= th.minThroughputKbps
    && lossPct < th.maxLossPct
    && jitter < th.maxJitterMs;
};

// 丢包率百分比
export const lossPctOf = (q?: QualityData): number => {
  if (!q) return 0;
  const total = q.packetsReceived + q.packetsLost;
  return total > 0 ? (q.packetsLost / total) * 100 : 0;
};

// 状态图标
export const StatusIcon: React.FC<{ status: string; isWinner?: boolean }> = ({ status, isWinner }) => {
  if (isWinner) return <Check size={12} className="text-emerald-400" />;
  switch (status) {
    case 'pending': return <Clock size={12} className="text-zinc-500" />;
    case 'offer_sent':
    case 'offer_received': return <Radio size={12} className="text-blue-400 animate-pulse" />;
    case 'racing': return <Wifi size={12} className="text-amber-400 animate-pulse" />;
    case 'testing': return <Zap size={12} className="text-cyan-400 animate-pulse" />;
    case 'connected': return <Check size={12} className="text-emerald-400" />;
    case 'failed': return <WifiOff size={12} className="text-red-400" />;
    case 'cancelled': return <X size={12} className="text-zinc-600" />;
    default: return <Clock size={12} className="text-zinc-500" />;
  }
};
